/* eslint-disable react-hooks/exhaustive-deps */

import { FC, useEffect, useState } from 'react';

import { Listing as ListingType } from '../Listings/Listings.types';

import { StyledListingCard } from './Listings.styles';

import {
    agentLogoTracking,
    agentPhoneTracking,
    agentContactTracking,
    listingTitleTracking,
    listingPriceTracking,
    listingDescriptionTracking,
    listingSpecTracking,
    listingTransportTracking,
    phoneNumberRevealTracking
} from './Listings.tracking';

import {
    SearchPageType,
    Favourites,
    AnalyticsTaxonomy
} from '../SearchContext';
import { useInView } from 'react-intersection-observer';

const removeFavouriteLabel = 'Remove this listing from your favourites';
const saveFavouriteLabel = 'Save this listing to your favourites';
const viewsLabel = 'views';
const viewsPeriodLabel = 'in last 30 days';

export type TrackingHandler = (props: {
    price: string;
    listingId: string;
}) => void;

export type Props = {
    key: string;
    dataTestId: string;
    index: number;
    searchIdentifier: string;
    searchType: SearchPageType;
    overrideViewsWithAvailability: boolean;
    favouritesListings: Favourites['listings'];
    onImageClick: () => void;
    onCardClick: () => void;
    onCardEcommerceClick: () => void;
    onListingView: () => void;
    onSaveClick: TrackingHandler;
    onPhoneClick: TrackingHandler;
    onContactClick: TrackingHandler;
    onPhoneRevealClick: TrackingHandler;
    analyticsTaxonomy: AnalyticsTaxonomy | undefined;
    adSlot?: JSX.Element | false;
    showHalo: boolean;
    presentationType?: string;
    isPhoneClickToRevealEnabled: boolean;
} & ListingType;

export const Listing: FC<Props> = (props) => {
    const {
        listingId,
        flag,
        underOffer,
        onImageClick,
        onCardClick,
        onCardEcommerceClick,
        onListingView,
        onSaveClick,
        onPhoneClick,
        onContactClick,
        onPhoneRevealClick,
        availableFrom,
        availableFromLabel,
        priceDrop,
        index,
        searchIdentifier,
        searchType,
        overrideViewsWithAvailability,
        address,
        branch,
        features,
        highlights,
        image,
        isPremium,
        listingUris,
        numberOfFloorPlans,
        numberOfImages,
        numberOfVideos,
        numberOfViews,
        otherPropertyImages,
        price,
        priceTitle,
        publishedOn,
        publishedOnLabel,
        tags,
        title,
        transports,
        dataTestId,
        favouritesListings,
        adSlot,
        presentationType,
        showHalo,
        isPhoneClickToRevealEnabled
    } = props;

    const [isFavourite, setIsFavourite] = useState(false);
    const { ref, inView } = useInView({
        triggerOnce: true
    });

    useEffect(() => {
        const favourite = favouritesListings.includes(listingId);

        setIsFavourite(favourite);
    }, [favouritesListings]);

    useEffect(() => {
        if (inView) onListingView();
    }, [inView]);

    return (
        <div
            key={`listing_${listingId}`}
            data-testid={`${dataTestId}_listing_${listingId}`}
            id={`listing_${listingId}`}
            ref={ref}
        >
            <StyledListingCard
                listingId={listingId}
                data-testid={dataTestId}
                useLazyImage={index >= 1}
                flag={flag}
                underOffer={underOffer}
                isHelptoBuy={false}
                isFavourite={isFavourite}
                onSaveClick={() => {
                    onSaveClick({ price, listingId });
                }}
                contactOnClick={() => {
                    onContactClick?.({ price, listingId });
                    agentContactTracking();
                }}
                phoneOnClick={() => {
                    onPhoneClick?.({ price, listingId });
                    agentPhoneTracking();
                }}
                phoneRevealOnClick={() => {
                    onPhoneRevealClick?.({ price, listingId });
                    phoneNumberRevealTracking();
                }}
                titleOnClick={listingTitleTracking}
                priceOnClick={listingPriceTracking}
                descriptionOnClick={listingDescriptionTracking}
                specOnClick={listingSpecTracking}
                transportInfoOnClick={listingTransportTracking}
                agentLogoOnClick={agentLogoTracking}
                imageOnClick={onImageClick}
                cardOnClick={onCardClick}
                onCardEcommerceClick={onCardEcommerceClick}
                removeFavouriteLabel={removeFavouriteLabel}
                saveFavouriteLabel={saveFavouriteLabel}
                viewsLabel={viewsLabel}
                viewsPeriodLabel={viewsPeriodLabel}
                searchIdentifier={searchIdentifier}
                availableFrom={availableFrom}
                availableFromLabel={availableFromLabel}
                searchType={searchType}
                overrideViewsWithAvailability={overrideViewsWithAvailability}
                priceDrop={priceDrop}
                address={address}
                branch={branch}
                features={features}
                highlights={highlights}
                image={image}
                isPremium={isPremium}
                listingUris={listingUris}
                numberOfFloorPlans={numberOfFloorPlans}
                numberOfImages={numberOfImages}
                numberOfVideos={numberOfVideos}
                numberOfViews={numberOfViews}
                otherPropertyImages={otherPropertyImages}
                price={price}
                priceTitle={priceTitle}
                publishedOn={publishedOn}
                publishedOnLabel={publishedOnLabel}
                tags={tags}
                title={title}
                transports={transports}
                showHalo={showHalo}
                presentationType={presentationType}
                isPhoneClickToRevealEnabled={isPhoneClickToRevealEnabled}
            />
            {adSlot}
        </div>
    );
};
